import {
	booleanAttribute,
	Component,
	computed,
	EventEmitter,
	forwardRef,
	Input,
	OnInit,
	Output,
	QueryList,
	ViewChildren,
	ChangeDetectionStrategy,
} from "@angular/core"
import { COMMON_IMPORTS } from "../common-imports"
import { Test, Testquestion, Testscale } from "@app/definitions/types"
import { GenInputComponent } from "@app/gen-inputs/gen-input/gen-input.component"
import { Modalable } from "@app/Modalable"
import { pluck } from "@app/services/api.service"
import { LangService } from "@app/services/lang.service"
import { ValidatorService } from "@app/services/validator.service"
import { LangValPipe } from "@app/utils/lang-val.pipe"
import { GenButtonGroupComponent } from "@app/gen-inputs/gen-button-group/gen-button-group.component"
import { YesNo } from "@app/utils/utils.component"
import { ModalWrapComponent } from "@app/modal-wrap/modal-wrap.component"
import { ConfirmComponent } from "@app/confirm/confirm.component"

const addRemoveArr = (arr: any[], item: any) =>
	arr.includes(item) ? arr.filter((it) => it !== item) : [...arr, item]

@Component({
	selector: "app-testscales",
	imports: [
		COMMON_IMPORTS,
		forwardRef(() => TestscaleComponent),
		ModalWrapComponent,
		ConfirmComponent,
	],
	changeDetection: ChangeDetectionStrategy.Eager,
	template: `
		<div class="main-container testscale-tbl">
			<!--      <div>-->
			<!--        @for(fieldHeader of fieldHeaders; track fieldHeader){-->
			<!--        <div>{{ fieldHeader | langVal }}</div>-->
			<!--        }-->
			<!--      </div>-->
			<button
				class="btn btn-icon"
				titleLang="add"
				(click)="isNewOpen = !isNewOpen"
				icon="plus"
			></button>
			@if (isNewOpen) {
				<div
					testscale
					class="new-row-scales d-flex"
					isNew
					[row]="newRow"
					[numedQuestions]="numedQuestions"
					(onUpdate)="handleUpdate(newRow, $event)"
					(onAddRow)="handleAddRow()"
				></div>
			}
			@for (row of rows; track row.id) {
				<div
					testscale
					class="new-row-scales d-flex"
					[numedQuestions]="numedQuestions"
					[row]="row"
					(onUpdate)="handleUpdate(row, $event)"
					(onDelete)="modalProps = { name: 'confirm-delete', id: $event }"
				></div>
			}
		</div>
		@switch (modalProps?.name) {
			@case ("confirm-delete") {
				<app-modal-wrap
					(onClose)="modalProps = null"
					title="do_you_confirm_delete"
					modalWrapperClass="confirm-modal-vert-center modal-narrow-width"
				>
					<app-confirm (onClose)="handleDelete($event)" />
				</app-modal-wrap>
			}
		}
	`,
})
export class TestscalesComponent extends Modalable {
	modalProps: any = null
	@Input() test_id: number = 0

	newRow: Testscale = new Testscale()
	rows: Testscale[] = []
	numedQuestions: Testquestion[] = []
	isNewOpen: boolean = false
	fieldHeaders: string[] = [
		"name",
		"calculation_method",
		"remarks",
		"questions",
		"avg_sd",
		"interpretation",
	] /*"scale_id",*/

	async ngOnInit() {
		const res: any = await this.store.get_gen_items("testscales", {
			filterFieldApiObject: {
				test_id: this.test_id,
			},
		})
		this.rows = res.testscales
		const qRes = await this.store.get_gen_items("testquestions")
		this.numedQuestions = qRes.testquestions
			.filter(
				(row: Testquestion) =>
					row.test_id === this.test_id && Number(row.question_num)
			)
			.toSorted(
				(a: Testquestion, b: Testquestion) => a.question_num - b.question_num
			)
	}
	async handleUpdate(row: any, { fieldName, value }) {
		row[fieldName] = value
		if (row.id) {
			const res = await this.apiService.save_gen_field(
				"testscales",
				row.id,
				fieldName,
				value
			)
			this.modalService.openToast(
				res.success ? "updated_successfully" : "save_failed"
			)
		}
	}
	async handleAddRow() {
		const res: any = await this.apiService.save_gen_item("testscales", {
			...this.newRow,
			test_id: this.test_id,
		})
		if (res?.id) {
			this.rows = [...this.rows, res]
			this.newRow = new Testscale()
		}
		this.modalService.openToast(
			res?.id ? "created_successfully" : "save_failed"
		)
	}
	async handleDelete(approved: boolean) {
		const { id } = this.modalProps
		this.modalProps = null
		if (!approved) {
			return
		}

		const res: any = await this.apiService.delete_gen_item("testscales", id)
		if (res.success) {
			this.rows = this.rows.filter((row) => row.id !== id)
		}
		this.modalService.openToast(
			res.success ? "delete_successful" : "delete_failed "
		)
	}
}

@Component({
	selector: "[testscale]",
	imports: [
		COMMON_IMPORTS,
		GenInputComponent,
		LangValPipe,
		GenButtonGroupComponent,
		forwardRef(() => TestscaleQuestionsChecksComponent),
		forwardRef(() => TestscaleTblFieldComponent),
	],
	changeDetection: ChangeDetectionStrategy.Eager,
	templateUrl: "./testscale.component.html",
})
export class TestscaleComponent {
	@Input({ transform: booleanAttribute }) isNew: boolean = false
	@Input() row: Testscale
	@Input() numedQuestions: Testquestion[] = []
	@Output() onUpdate: EventEmitter<any> = new EventEmitter()
	@Output() onDelete: EventEmitter<number> = new EventEmitter()
	@Output() onAddRow: EventEmitter<null> = new EventEmitter()

	errors: any = {}
	isFormSubmittable: boolean = false
	validationFields: any = {
		name: { not_empty: null },
	}

	constructor(
		public validator: ValidatorService,
		public lang: LangService
	) {}

	hasQuestions() {
		if (!this.row.questions) {
			return false
		}
		const value = JSON.parse(this.row.questions)
		return value?.length
	}
	validateEntireForm() {
		//validate all fields and detrmine if can be submitted (or has errors)
		this.errors = {}
		this.validator.validateEntireForm(
			this.row,
			this.errors,
			this.validationFields
		)
		if (!this.hasQuestions()) {
			this.errors.questions = this.lang.getVal("fill_questions_numbers")
		}
		this.isFormSubmittable = this.validator.formIsValid(this.errors)
	}

	handleChange(fieldName: string, value: string) {
		this.onUpdate.emit({
			fieldName,
			value,
		})
		if (this.isNew) {
			this.validateEntireForm()
		}
	}
	handleDelete(id: number) {
		this.onDelete.emit(id)
	}
	handleAddRow() {
		this.onAddRow.emit()
	}
}

@Component({
	selector: "app-testscale-questions-checks",
	imports: [COMMON_IMPORTS, YesNo],
	changeDetection: ChangeDetectionStrategy.Eager,
	template: `
		<div class="flex flex-wrap ">
			@for (question of numedQuestions; track question.id) {
				<div class="flex-v-center mx-2">
					<label>{{ question.question_num }}</label>
					<app-yes-no
						(onClick)="check(question.question_num)"
						[isCheck]="checked.includes(question.question_num)"
					/>
				</div>
			}
		</div>
	`,
})
export class TestscaleQuestionsChecksComponent {
	@Input() value: any
	@Output() onUpdate: EventEmitter<string> = new EventEmitter()
	@Input() numedQuestions: Testquestion[] = []

	checked: number[] = []

	ngOnInit() {
		if (!this.value) {
			return
		}
		const res = JSON.parse(this.value)
		this.checked = res?.length ? res : []
	}
	check(order_num: number) {
		this.checked = addRemoveArr(this.checked, order_num)
		this.value = JSON.stringify(this.checked)
		this.onUpdate.emit(this.value)
	}
}

@Component({
	selector: "app-testscale-tbl-field",
	imports: [COMMON_IMPORTS, GenInputComponent, LangValPipe],
	changeDetection: ChangeDetectionStrategy.Eager,
	templateUrl: "./testscale-tbl-field.component.html",
})
export class TestscaleTblFieldComponent {
	@Input({ transform: booleanAttribute }) isAvg: boolean
	@Input() value: any
	@Output() onUpdate: EventEmitter<string> = new EventEmitter()
	@ViewChildren(GenInputComponent) genInputs

	fields: string[] = []
	items: any[] = []

	ngOnInit() {
		this.fields = this.isAvg
			? ["population", "avg", "sd"]
			: ["interpretation", "min", "max"]
		if (!this.value) {
			return
		}
		const res = JSON.parse(this.value)
		this.items = res?.length ? res : []
		this.items.forEach((row, count) => (row.id = count + 1))
	}
	update() {
		const preppedObj = this.items.map((row) =>
			pluck(row, this.fields.join(","))
		)
		this.value = JSON.stringify(preppedObj)
		this.onUpdate.emit(this.value)
	}
	getTopId() {
		return this.items.reduce((prev, cur) => Math.max(prev, cur.id), 0)
	}
	handleAddRow() {
		const newRow: any = { id: this.getTopId() + 1 }
		this.fields.forEach((f, count) => (newRow[f] = count ? 0 : ""))
		this.items = [...this.items, newRow]
		const inputClassName = `input-${newRow.id}-${this.fields[0]}`
		setTimeout(() => {
			const inp = this.genInputs.find((inp) =>
				inp.className.includes(inputClassName)
			)
			if (inp) {
				inp.focus()
			}
		})
	}
	handleUpdate(row: any, fieldName: string, value: any) {
		row[fieldName] = value
		this.update()
	}
	handleDelete(id: number) {
		this.items = this.items.filter((it) => it.id !== id)
		this.update()
	}
}
